import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { Country, MapCountryNamePipe } from "@domain/user/locale/country";
import { HighlightSearchDirective } from "@odx/angular/cdk/autocomplete-control";
import { AutocompleteSearchFilterPipe } from "@odx/angular/components/autocomplete";
import { FormFieldComponent } from "@odx/angular/components/form-field";
import { delay, distinctUntilChanged, of, Subject, takeUntil } from "rxjs";

export interface CountryMap {
  countryCode: string;
  localizedName: string;
}

@Component({
  selector: "app-country-selection-form-field",
  imports: [AutocompleteSearchFilterPipe, FormFieldComponent, UiKitModule, HighlightSearchDirective, ReactiveFormsModule],
  providers: [MapCountryNamePipe],
  templateUrl: "./country-selection-form-field.component.html",
  styleUrl: "./country-selection-form-field.component.scss",
})
export class CountrySelectionFormFieldComponent implements OnChanges, OnInit, OnDestroy {
  @Input() countries: Country[] = [];
  @Input() formGroup!: FormGroup;
  @Input() disabled: boolean = false;
  @Input() label?: string;
  @Output() selectCountry = new EventEmitter<string>();

  protected readonly controlName = "country";
  protected localizedCountryNames: string[] = [];
  protected noCountryFoundMessage: string = $localize`:@@selectLanguage.search.noLanguage:Keine Sprache gefunden`;

  private mapCountryNamePipe = inject(MapCountryNamePipe);
  private countryMaps: CountryMap[] = [];
  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.localizeCountries();

    const countryControl = this.formGroup?.get(this.controlName);
    if (!countryControl) return;

    countryControl.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((value) => {
      this.syncFromControlValue(value);
    });

    this.syncFromControlValue(countryControl.value);
  }

  ngOnChanges() {
    this.localizeCountries();
    this.syncFromControlValue(this.formGroup?.get(this.controlName)?.value);
  }

  private localizeCountries() {
    this.localizedCountryNames = [];
    this.countryMaps = [];
    this.mapCountryCodeToLocalizedName();
    const names = this.countryMaps.map((c) => c.localizedName);
    of(names)
      .pipe(delay(0))
      .subscribe((n) => {
        this.localizedCountryNames = n.sort((a, b) => a.localeCompare(b));
      });
  }

  private mapCountryCodeToLocalizedName() {
    for (const country of this.countries) {
      const localizedName = this.mapCountryNamePipe.transform(country.countryCode);
      this.countryMaps.push({ countryCode: country.countryCode, localizedName: localizedName });
    }
  }
  private mapLocalizedNameToCountryCode(localizedName: string | null | undefined) {
    for (const countryMap of this.countryMaps) {
      if (countryMap.localizedName === localizedName) return countryMap.countryCode;
    }

    return undefined;
  }

  private syncFromControlValue(localizedName: string | null | undefined) {
    if (!localizedName) return;

    const countryCode = this.mapLocalizedNameToCountryCode(localizedName);
    if (countryCode) this.selectCountry.emit(countryCode);
  }

  onSelect(country: string) {
    const countryCode = this.mapLocalizedNameToCountryCode(country);
    if (countryCode) this.selectCountry.emit(countryCode);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
